import React, { createContext, useCallback, useContext, useRef, useState, useEffect } from "react";
import axios from "axios";
import { Phone, Mail, ShieldCheck, X, Loader2 } from "lucide-react";
import "./OTPGate.css";
import "./OTPModal.css";

/**
 * Universal OTP modal + hook. Any form calls `requireOtp(phone)` and gets back
 * a `Promise<trust_token>` that the form attaches to its submit payload.
 *
 * Usage:
 *   const { requireOtp } = useRequireOtp();
 *   const onSubmit = async () => {
 *     const trustToken = await requireOtp(form.phone);  // shows modal if needed
 *     await axios.post(API, { ...form, trust_token: trustToken });
 *   };
 *
 * If the user already has a 30-day trust cookie for this phone, the modal
 * never opens — the promise resolves instantly with the cached token. This is
 * core to the "verify quality leads without annoying repeat users" UX.
 */

const API = (typeof window !== "undefined" && window.location?.origin) || process.env.REACT_APP_BACKEND_URL;
const TRUST_KEY = (e164) => `ac_otp_trust_${e164}`;

const OTPContext = createContext(null);

export const OTPModalProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [stage, setStage] = useState("idle"); // idle|sending|code|verifying
  const [code, setCode] = useState("");
  const [channel, setChannel] = useState("");
  const [phoneE164, setPhoneE164] = useState("");
  const [error, setError] = useState("");
  const honeypotRef = useRef("");
  const mountedAt = useRef(Date.now());
  const resolverRef = useRef(null);

  const reset = useCallback(() => {
    setOpen(false);
    setStage("idle");
    setCode("");
    setError("");
    setChannel("");
    setPhoneE164("");
    setEmail("");
    setCountry("");
    honeypotRef.current = "";
  }, []);

  const requireOtp = useCallback(async (rawPhone, rawEmail = "", rawCountry = "") => {
    if (!rawPhone) {
      throw new Error("requireOtp called without phone");
    }
    // Check existing trust cookie (across multiple e164 candidates so users
    // who entered a number variant still benefit from a recent verification).
    const guess = (() => {
      const stripped = rawPhone.replace(/\D/g, "");
      if (rawPhone.startsWith("+")) return rawPhone;
      if (stripped.length === 10) return `+1${stripped}`;
      if (stripped.length === 11 && stripped.startsWith("1")) return `+${stripped}`;
      return null;
    })();
    if (guess) {
      const cached = localStorage.getItem(TRUST_KEY(guess));
      if (cached) return cached;
    }

    setPhone(rawPhone);
    setEmail(rawEmail || "");
    setCountry(rawCountry || "");
    mountedAt.current = Date.now();
    setOpen(true);
    setStage("idle");

    return new Promise((resolve, reject) => {
      resolverRef.current = { resolve, reject };
    });
  }, []);

  const sendCode = async () => {
    setError("");
    setStage("sending");
    try {
      const r = await axios.post(`${API}/api/otp/send`, {
        phone,
        email,
        country,
        honeypot: honeypotRef.current,
        ms_on_form: Date.now() - mountedAt.current,
        trust_token: "",
      });
      const data = r.data;
      setChannel(data.channel);
      setPhoneE164(data.phone_e164 || "");
      if (data.channel === "trusted" || data.channel === "bypass") {
        // Resolve immediately
        const tok = data.trust_token || "trusted";
        if (data.phone_e164) localStorage.setItem(TRUST_KEY(data.phone_e164), tok);
        resolverRef.current?.resolve(tok);
        reset();
      } else {
        setStage("code");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send code. Please try again.";
      setError(detail);
      setStage("idle");
    }
  };

  const verifyCode = async () => {
    setError("");
    setStage("verifying");
    try {
      const r = await axios.post(`${API}/api/otp/verify`, {
        phone: phoneE164 || phone,
        email: channel === "email_fallback" ? email : "",
        code,
      });
      if (r.data.trust_token && r.data.phone_e164) {
        localStorage.setItem(TRUST_KEY(r.data.phone_e164), r.data.trust_token);
      }
      resolverRef.current?.resolve(r.data.trust_token);
      reset();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Invalid code. Try again.";
      setError(detail);
      setStage("code");
    }
  };

  const cancel = () => {
    resolverRef.current?.reject(new Error("OTP cancelled"));
    reset();
  };

  // ESC closes modal
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") cancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <OTPContext.Provider value={{ requireOtp }}>
      {children}
      {open && (
        <div className="ac-otpm-backdrop" onClick={cancel} role="dialog" aria-modal="true" data-testid="otp-modal">
          <div className="ac-otpm-card" onClick={(e) => e.stopPropagation()}>
            <button onClick={cancel} className="ac-otpm-close" aria-label="Close" data-testid="otp-close"><X className="w-4 h-4" /></button>
            {/* Honeypot */}
            <label className="ac-otp-honeypot" aria-hidden="true">
              Leave empty:
              <input type="text" tabIndex={-1} autoComplete="off"
                onChange={(e) => { honeypotRef.current = e.target.value; }} />
            </label>

            {stage === "idle" && (() => {
              const isIntl = !!country && country.trim().toLowerCase() !== "canada";
              return (
                <>
                  <h3 className="ac-otpm-title">{isIntl ? "Verify your email" : "Verify your phone"}</h3>
                  <p className="ac-otpm-msg">
                    {isIntl
                      ? "We'll email you a quick 4-digit code to make sure you're a real person. No spam — your email is verified, not stored for marketing."
                      : "We send a quick 4-digit SMS code to make sure you're a real person. No spam, no charges — your number is verified, not stored for marketing."}
                  </p>
                  <div className="ac-otpm-phone">{isIntl ? email : phone}</div>
                  <button onClick={sendCode} className="ac-otp-btn-primary" data-testid="otp-send-btn">
                    {isIntl ? <><Mail className="w-4 h-4" /> Email 4-digit code</> : <><Phone className="w-4 h-4" /> Send 4-digit code</>}
                  </button>
                  {error && <p className="ac-otp-error">{error}</p>}
                </>
              );
            })()}

            {stage === "sending" && (
              <div className="ac-otp-pending"><Loader2 className="w-5 h-5 animate-spin" /> Sending…</div>
            )}

            {stage === "code" && (
              <>
                <h3 className="ac-otpm-title">Enter the code</h3>
                <div className="ac-otp-callout">
                  <ShieldCheck className="w-5 h-5" />
                  {channel === "email_fallback"
                    ? <span>We've emailed a 4-digit code to <strong>{email || "your email"}</strong>. Enter it below.</span>
                    : <span>Code texted to <strong>{phoneE164}</strong></span>}
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  maxLength={4}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="0000"
                  className="ac-otp-code-input"
                  data-testid="otp-code-input"
                />
                {error && <p className="ac-otp-error">{error}</p>}
                <div className="ac-otp-actions">
                  <button onClick={verifyCode} disabled={code.length < 4} className="ac-otp-btn-primary" data-testid="otp-verify-btn">
                    Verify
                  </button>
                  <button onClick={sendCode} className="ac-otp-btn-link" data-testid="otp-resend">Resend code</button>
                </div>
              </>
            )}

            {stage === "verifying" && (
              <div className="ac-otp-pending"><Loader2 className="w-5 h-5 animate-spin" /> Verifying…</div>
            )}
          </div>
        </div>
      )}
    </OTPContext.Provider>
  );
};

export const useRequireOtp = () => {
  const ctx = useContext(OTPContext);
  if (!ctx) throw new Error("useRequireOtp must be used inside <OTPModalProvider>");
  return ctx;
};
